import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Shuffle, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { absoluteUrl } from "@/lib/site";
import { ACTIVATIONS_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

export const Route = createFileRoute("/activations/calendar")({
  component: CalendarPage,
  head: () => ({
    ...seoMeta({
      title: "Activation Calendar · KAfrequency",
      description:
        "Every day, a new activation. Navigate the year and enter any day's transmission.",
      path: "/activations/calendar",
    }),
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: `${absoluteUrl("/activations/calendar")}#calendar`,
          url: absoluteUrl("/activations/calendar"),
          name: "Activation Calendar",
          description: "The day-by-day calendar of KAfrequency activations by Buddha KA.",
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACTIVATIONS_ID } },
        }),
      ),
      ldScript(
        breadcrumbs([["Activations", "/activations"], ["Calendar", "/activations/calendar"]]),
      ),
    ],
  }),
});

interface DayRow { date: string; slug: string; title: string; }

function CalendarPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<DayRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const start = new Date(year, 0, 1).toISOString();
      const end = new Date(year + 1, 0, 1).toISOString();
      const { data } = await supabase
        .from("activations")
        .select("slug, title, publish_at")
        .gte("publish_at", start)
        .lt("publish_at", end)
        .order("publish_at", { ascending: true });
      if (cancelled) return;
      const mapped: DayRow[] = (data ?? []).map((r) => ({
        date: new Date(r.publish_at!).toISOString().slice(0, 10),
        slug: r.slug,
        title: r.title,
      }));
      setRows(mapped);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [year]);

  const byDate = useMemo(() => {
    const m = new Map<string, DayRow>();
    for (const r of rows) if (!m.has(r.date)) m.set(r.date, r);
    return m;
  }, [rows]);

  async function jumpToday() {
    const today = new Date().toISOString().slice(0, 10);
    const hit = byDate.get(today);
    if (hit) navigate({ to: "/activations/$slug", params: { slug: hit.slug } });
    else navigate({ to: "/activations" });
  }

  async function shiftDay(days: number) {
    const today = new Date();
    today.setDate(today.getDate() + days);
    const key = today.toISOString().slice(0, 10);
    const hit = byDate.get(key);
    if (hit) navigate({ to: "/activations/$slug", params: { slug: hit.slug } });
  }

  async function jumpRandom() {
    if (rows.length === 0) return;
    const pick = rows[Math.floor(Math.random() * rows.length)];
    navigate({ to: "/activations/$slug", params: { slug: pick.slug } });
  }

  return (
    <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
      <Link to="/activations" className="text-xs uppercase tracking-[0.3em] text-gold">← Activations</Link>
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Calendar</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl">The year in transmissions</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setYear((y) => y - 1)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border hairline hover:border-gold" aria-label="Previous year">
            <ChevronLeft size={16} />
          </button>
          <span className="font-serif text-2xl text-gold-luminous">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border hairline hover:border-gold" aria-label="Next year">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => shiftDay(-1)} className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold">Yesterday</button>
        <button onClick={jumpToday} className="rounded-full border-gold border bg-gold text-obsidian px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold">
          <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> Today</span>
        </button>
        <button onClick={() => shiftDay(1)} className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold">Tomorrow</button>
        <button onClick={jumpRandom} className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-gold">
          <span className="inline-flex items-center gap-1"><Shuffle size={12} /> Random</span>
        </button>
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-muted-foreground">Loading calendar…</p>
      ) : (
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 12 }, (_, m) => (
            <MonthCard key={m} year={year} month={m} byDate={byDate} />
          ))}
        </div>
      )}
    </section>
  );
}

function MonthCard({ year, month, byDate }: { year: number; month: number; byDate: Map<string, DayRow> }) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = first.getDay();
  const label = first.toLocaleDateString("en-US", { month: "long" });
  const todayKey = new Date().toISOString().slice(0, 10);

  return (
    <div className="rounded-xl border hairline bg-charcoal p-5">
      <h3 className="font-serif text-lg text-gold-luminous">{label}</h3>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
        {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: startDay }, (_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const hit = byDate.get(key);
          const isToday = key === todayKey;
          const base = "aspect-square flex items-center justify-center text-xs rounded";
          if (hit) {
            return (
              <Link
                key={day}
                to="/activations/$slug"
                params={{ slug: hit.slug }}
                title={hit.title}
                className={`${base} border-gold border bg-gold/10 text-gold hover:bg-gold hover:text-obsidian ${isToday ? "ring-1 ring-gold" : ""}`}
              >
                {day}
              </Link>
            );
          }
          return (
            <div key={day} className={`${base} text-muted-foreground/50 ${isToday ? "ring-1 ring-gold/50" : ""}`}>
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}
